import { detectCards } from './detect.js';

const DETECT_MAX = 900;
export const EBAY_SIZE = 1600;
const EBAY_FILL = 0.9;

export function R(b) {
  return b.manual ? b : b.geo.card;
}

export function corners(b) {
  const c = Math.cos(b.angle);
  const s = Math.sin(b.angle);
  const hw = b.w / 2;
  const hh = b.h / 2;
  return [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]].map(([u, v]) => [
    b.cx + u * c - v * s,
    b.cy + u * s + v * c,
  ]);
}

function inside(x, y, g) {
  const c = Math.cos(g.angle);
  const s = Math.sin(g.angle);
  const dx = x - g.cx;
  const dy = y - g.cy;
  return Math.abs(dx * c + dy * s) <= g.w / 2 && Math.abs(-dx * s + dy * c) <= g.h / 2;
}

export function sameCard(a, b) {
  const p = R(a);
  const q = R(b);
  return inside(p.cx, p.cy, q) || inside(q.cx, q.cy, p);
}

export function sortBoxes(scan) {
  const boxes = scan.boxes;
  if (!boxes.length) return;
  const rowTol = Math.min(...boxes.map((x) => Math.min(R(x).w, R(x).h))) * 0.5;
  boxes.sort((p, q) => (
    Math.abs(R(p).cy - R(q).cy) < rowTol ? R(p).cx - R(q).cx : R(p).cy - R(q).cy
  ));
}

export function normAngle(b) {
  while (b.angle > Math.PI / 4) {
    b.angle -= Math.PI / 2;
    [b.w, b.h] = [b.h, b.w];
  }
  while (b.angle <= -Math.PI / 4) {
    b.angle += Math.PI / 2;
    [b.w, b.h] = [b.h, b.w];
  }
}

export function toManual(b) {
  if (b.manual) return;
  const g = R(b);
  Object.assign(b, {
    cx: g.cx,
    cy: g.cy,
    w: g.w,
    h: g.h,
    angle: g.angle,
    manual: true,
    edited: true,
  });
  delete b.geo;
}

export function turnsFor(b) {
  return b.rot || 0;
}

export function fileName(n, st) {
  return `${st.prefix}_${String(n).padStart(2, '0')}.${st.fmt}`;
}

export function findBoxes(scan, sensitivity) {
  const { img } = scan;
  const s = Math.min(1, DETECT_MAX / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.round(img.naturalWidth * s);
  const h = Math.round(img.naturalHeight * s);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, w, h);
  const found = detectCards(ctx.getImageData(0, 0, w, h).data, w, h, { sensitivity });
  const scaleUp = (q) => ({
    cx: q.cx / s,
    cy: q.cy / s,
    w: q.w / s,
    h: q.h / s,
    angle: q.angle,
  });
  return found.map((r) => ({
    geo: { card: scaleUp(r.inner), loader: scaleUp(r.outer) },
    cardLike: r.cardLike,
    on: r.cardLike,
    rot: null,
  }));
}

export function allOutputs(scans) {
  const out = [];
  let n = 0;
  for (const scan of scans) {
    for (const box of scan.boxes) {
      if (box.on) out.push({ scan, box, n: ++n });
    }
  }
  return out;
}

export function outputSize(box, st) {
  const k = ((turnsFor(box) % 4) + 4) % 4;
  const g = R(box);
  const m = Math.min(g.w, g.h) * st.pad;
  const fw = Math.round(g.w + 2 * m);
  const fh = Math.round(g.h + 2 * m);
  if (st.ebay) return [EBAY_SIZE, EBAY_SIZE];
  return k % 2 ? [fh, fw] : [fw, fh];
}

export function cropTo(box, img, st, maxSide) {
  const k = ((turnsFor(box) % 4) + 4) % 4;
  const b = R(box);
  const m = Math.min(b.w, b.h) * st.pad;
  const w = Math.max(4, b.w + 2 * m);
  const h = Math.max(4, b.h + 2 * m);
  const scale = maxSide ? Math.min(1, maxSide / Math.max(w, h)) : 1;
  const W0 = Math.round(w * scale);
  const H0 = Math.round(h * scale);
  const [OW, OH] = k % 2 ? [H0, W0] : [W0, H0];
  const canvas = document.createElement('canvas');
  canvas.width = OW;
  canvas.height = OH;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, OW, OH);
  ctx.imageSmoothingQuality = 'high';
  ctx.translate(OW / 2, OH / 2);
  ctx.rotate(k * Math.PI / 2);
  ctx.scale(scale, scale);
  ctx.rotate(-b.angle);
  ctx.translate(-b.cx, -b.cy);
  ctx.drawImage(img, 0, 0);
  return canvas;
}

export function output(box, img, st, previewSide) {
  if (!st.ebay) return cropTo(box, img, st, previewSide);
  const S = previewSide || EBAY_SIZE;
  const crop = cropTo(box, img, st);
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = st.bg;
  ctx.fillRect(0, 0, S, S);
  const k = (S * EBAY_FILL) / Math.max(crop.width, crop.height);
  const w = Math.round(crop.width * k);
  const h = Math.round(crop.height * k);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(crop, Math.round((S - w) / 2), Math.round((S - h) / 2), w, h);
  return canvas;
}

export function toBlob(canvas, fmt) {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, fmt === 'png' ? 'image/png' : 'image/jpeg', 0.95);
  });
}
